import { supabase } from '../lib/supabase';
import { api } from '@/lib/api';
import { tokenManager } from '@/lib/tokenManager';

interface PasswordValidation {
  isValid: boolean;
  message?: string;
}

export const authService = {
  validatePassword(password: string): PasswordValidation {
    if (password.length < 8) {
      return {
        isValid: false,
        message: 'Password must be at least 8 characters long'
      };
    }

    if (!password.trim()) {
      return {
        isValid: false,
        message: 'Password cannot be empty or only whitespace'
      };
    }

    return { isValid: true };
  },

  async signUp(email: string, password: string) {
    const validation = this.validatePassword(password);
    if (!validation.isValid) {
      throw new Error(validation.message);
    }

    try {
      // Sign up with Supabase
      const { data: { user }, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: import.meta.env.VITE_SUPABASE_CALLBACK_URL,
        }
      });

      if (error) throw error;
      if (!user) throw new Error('Signup failed - no user returned');

      return {
        message: 'Please check your email to verify your account',
        user: {
          email: user.email,
          id: user.id
        }
      };
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  },

  async login(email: string, password: string) {
    try {
      // Sign in with Supabase
      const { data: authData, error: supabaseError } = 
        await supabase.auth.signInWithPassword({
          email,
          password
        });

      if (supabaseError) throw supabaseError;

      // Get the session token
      const session = authData.session;
      if (!session) throw new Error('No session after login');

      // Get fresh token using TokenManager
      const token = await tokenManager.getToken(true);
      if (!token) throw new Error('Failed to get token');

      // Call our backend to get our JWT
      const response = await api.post('/auth/login', {
        token: token
      });

      // Store our backend's JWT
      localStorage.setItem('token', response.data.access_token);
      
      // Store the user profile for later use (including logout)
      localStorage.setItem('userProfile', JSON.stringify(response.data.profile));
      
      console.log('Saved user profile to localStorage:', response.data.profile);
      
      return { profile: response.data.profile };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: import.meta.env.VITE_SUPABASE_CALLBACK_URL,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
          response_type: 'code',
        }
      }
    });

    if (error) throw error;
    return data;
  },

  async handleAuthCallback(token: string) {
    try {
      // Exchange Supabase token for our JWT
      const response = await api.post('/auth/login', {
        token: token
      });

      // Cache the token
      localStorage.setItem('token', response.data.access_token);

      return {
        profile: response.data.profile
      };
    } catch (error) {
      console.error('Auth callback error:', error);
      throw error;
    }
  },

  async verifyToken(token: string) {
    try {
      const validToken = await tokenManager.getToken();
      if (!validToken) throw new Error('No valid token');

      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${validToken}` }
      });

      if (!response?.data) {
        tokenManager.clearCache();
        throw new Error('Failed to verify user');
      }

      return response.data;
    } catch (error) {
      tokenManager.clearCache();
      throw error;
    }
  },

  async logout(userId?: string) {
    try {
      // Use provided userId or try to get it from localStorage as fallback
      let userIdToUse = userId;
      
      if (!userIdToUse) {
        // Try to get user profile from store as fallback
        const userProfile = localStorage.getItem('userProfile');
        
        if (userProfile) {
          try {
            const profile = JSON.parse(userProfile);
            userIdToUse = profile.userId || profile.id || null;
          } catch (e) {
            console.warn('Could not parse user profile:', e);
          }
        }
      }
      
      // Clear local tokens
      tokenManager.clearCache();
      
      // Sign out from Supabase
      await supabase.auth.signOut({
        scope: 'local'
      });
      
      // Send logout request with user ID if available
      if (userIdToUse) {
        try {
          // Ensure user_id is sent as a string
          await api.post('/auth/logout', { 
            user_id: String(userIdToUse)
          });
        } catch (e: any) {
          console.warn('Backend logout failed:', e.message);
        }
      }
      
      // Clear any stored profile data
      localStorage.removeItem('userProfile');
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Even if backend logout fails, consider it a success if frontend tokens are cleared
      return { success: true };
    }
  },

  endSession() {
    tokenManager.clearCache();
  }
}; 